import {
  isCommandStep,
  isDefaultCommandStep,
  isMatrixCommandStep,
  isParallelCommandStep,
  isGroupStep,
  isIgnored,
  isWaitingOnConcurrencyGroup,
  isWaitingOnPlatformLimit,
  Step,
  CommandStep,
} from "~/lib/pipeline";
import { twMerge } from "tailwind-merge";
import { StepLink } from "~/components/build/Show/components/Sidebar/TreeNode/StepLink";
import { Label } from "~/components/build/Show/components/Sidebar/TreeNode/Label";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import Emojify from "~/components/shared/Emojify";
import {
  useMostRecentJob,
  useBuild,
  useMostRecentMatrixJobsForStep,
  useMostRecentParallelJobsForStep,
} from "~/components/build/Show/lib/BuildContext";
import CommandStepLabel from "~/components/build/Show/components/CommandStepLabel";
import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import { GroupStepCount } from "~/components/build/Show/components/GroupStepCount";
import { JobCount, StatsJobCount } from "~/components/build/Show/components/StatsJobCount";
import { Build, CommandJob } from "~/components/build/Show/lib/types";
import { ReactNode, useMemo } from "react";
import Duration from "~/components/shared/Duration";
import { StepRetries } from "~/components/build/Show/components/Sidebar/TreeNode/JobRetries";
import { RunDuration } from "~/components/job/Job/JobDuration";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";
import { stepQueryOptions } from "~/components/build/Show/lib/queries/step";
import { stepJobsQueryOptions } from "~/components/build/Show/lib/queries/stepJobs";
import { usePrefetchHandlers } from "~/components/build/Show/lib/usePrefetchHandlers";

interface Props {
  step: Step;
  onClick?: () => void;
  className?: string;
  active?: boolean;
  failed?: boolean;
  depth?: number;
}

const Count = (step: Step, build: Build) => {
  if (Features.ProgressiveBuildShow && (isMatrixCommandStep(step) || isParallelCommandStep(step))) {
    return <StatsJobCount step={step} />;
  }

  if (isGroupStep(step)) {
    return <GroupStepCount step={step} />;
  } else if (isMatrixCommandStep(step)) {
    return CountForMatrixStep(build, step);
  } else if (isParallelCommandStep(step)) {
    return CountForParallelStep(build, step);
  }
  return null;
};

const CountForMatrixStep = (build: Build, step: CommandStep) => {
  return <JobCount jobs={useMostRecentMatrixJobsForStep(step.uuid)} step={step} />;
};

const CountForParallelStep = (build: Build, step: CommandStep) => {
  return <JobCount jobs={useMostRecentParallelJobsForStep(step.uuid)} step={step} />;
};

/**
 * A catch-all step list item.
 */
/** @deprecated Use `~/components/build/Show/pages/steps/list/components/TreeNode/StepItem` instead. */
export function StepItem({ step, onClick, className, active, failed, depth = 0 }: Props) {
  const job = useMostRecentJob(step.uuid);
  const waitingOnConcurrencyGroup =
    isDefaultCommandStep(step) && isWaitingOnConcurrencyGroup(job?.state, step.statistics);
  const waitingOnPlatformLimit =
    isDefaultCommandStep(step) && isWaitingOnPlatformLimit(job?.state, step.statistics);
  const { build } = useBuild();
  const { missingDependencies } = useStepDependencies(step.uuid);

  const isStepIgnored = isIgnored(step);

  let label: ReactNode;
  if (isCommandStep(step)) {
    label = <CommandStepLabel {...step} />;
  } else {
    // Add a non-breaking space to prevent empty label from collapsing
    label = <Emojify text={step.label} className="empty:before:content-['\00a0']" />;
  }

  let duration: ReactNode | undefined;
  if (isDefaultCommandStep(step)) {
    if (Features.ProgressiveBuildShow && step.statistics?.latestJobStartedAt) {
      duration = (
        <Duration.Short
          from={step.statistics.latestJobStartedAt}
          to={step.statistics.latestJobFinishedAt || undefined}
        />
      );
    } else {
      if (job?.type === "script" && job.startedAt) {
        duration = <RunDuration job={job as CommandJob} />;
      }
    }
  }

  const shouldOpenStepDrawer = useMemo(() => {
    if (
      isParallelCommandStep(step) ||
      isMatrixCommandStep(step) ||
      isGroupStep(step) ||
      isStepIgnored
    ) {
      return false;
    }
    return true;
  }, [step, isStepIgnored]);

  const { onMouseEnter, onMouseLeave } = usePrefetchHandlers({
    queries: [stepQueryOptions(build, step.uuid), stepJobsQueryOptions(build, step.uuid)],
    enabled: Features.ProgressiveBuildShow && shouldOpenStepDrawer,
  });

  return (
    <StepLink
      state={step.state}
      onClick={onClick}
      className={className}
      type={step.type}
      to={resolveStepUrl(step.uuid, { open: shouldOpenStepDrawer })}
      active={active}
      failed={failed}
      depth={depth}
      disabled={isStepIgnored}
      title={isStepIgnored ? "Skipped steps cannot be viewed" : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Label
        info={isDefaultCommandStep(step) && <StepRetries step={step} />}
        duration={duration}
        status={
          <StateIcon
            {...step}
            className={twMerge(
              "-mr-0.5",
              textColorForStep({
                ...step,
                missingDependencies,
                waitingOnConcurrencyGroup,
                waitingOnPlatformLimit,
              }),
            )}
            missingDependencies={missingDependencies}
            waitingOnConcurrencyGroup={waitingOnConcurrencyGroup}
            waitingOnPlatformLimit={waitingOnPlatformLimit}
          />
        }
        count={Count(step, build)}
      >
        {label}
      </Label>
    </StepLink>
  );
}
